import React, { useState } from "react";
import { Landmark, Edit2, Save, X } from "lucide-react";
import { affiliateAPI } from "../../services/AdminPortal/affiliateAPI";
import { toast } from "react-hot-toast";

export default function BankAccountDetails({
  bankDetails = {},
  partnerId,
  onEdit,
  onSave,
}) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    accountHolderName: bankDetails?.accountHolderName || "",
    accountNumber: bankDetails?.accountNumber || "",
    bankName: bankDetails?.bankName || "",
    bankBranch: bankDetails?.bankBranch || "",
    ifscCode: bankDetails?.ifscCode || "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setEditMode(true);
    if (onEdit) onEdit();
  };

  const handleCancel = () => {
    setEditMode(false);
    setForm({
      accountHolderName: bankDetails?.accountHolderName || "",
      accountNumber: bankDetails?.accountNumber || "",
      bankName: bankDetails?.bankName || "",
      bankBranch: bankDetails?.bankBranch || "",
      ifscCode: bankDetails?.ifscCode || "",
    });
  };

  const handleSave = async () => {
    // Validate required fields
    const requiredFields = [
      "accountHolderName",
      "accountNumber",
      "bankName",
      "bankBranch",
      "ifscCode",
    ];
    const missingFields = requiredFields.filter(
      (field) => !form[field] || form[field].trim() === ""
    );

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      return;
    }

    setLoading(true);
    try {
      const response = await affiliateAPI.updateBankDetails(partnerId, form);

      if (response.success) {
        setEditMode(false);
        if (onSave) onSave(form);
        // Show success message
        toast.success("Bank details updated successfully!");
      } else {
        throw new Error(response.message || "Failed to update bank details");
      }
    } catch (error) {
      console.error("Error updating bank details:", error);
      toast.error(
        error.message || "Failed to update bank details. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#181818] rounded-lg p-6 border border-[#222] max-w-full mx-full mt-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <Landmark className="text-[#FFCA20] mr-2" size={22} />
          <h2 className="text-lg font-semibold">Bank Account Details</h2>
        </div>
        {editMode ? (
          <div className="flex gap-2">
            <button
              className="bg-green-500 text-white px-4 py-1 rounded flex items-center font-medium hover:bg-green-600 transition"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                "Saving..."
              ) : (
                <>
                  <span>Save</span> <Save className="ml-2 w-4 h-4" />
                </>
              )}
            </button>
            <button
              className="bg-gray-600 text-white px-4 py-1 rounded flex items-center font-medium hover:bg-gray-700 transition"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel <X className="ml-2 w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            className="bg-[#FFCA20] text-black px-4 py-1 rounded flex items-center font-medium hover:bg-yellow-400 transition"
            onClick={handleEdit}
          >
            Edit <Edit2 className="ml-2 w-4 h-4" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            Account Holder Name*
          </label>
          <input
            type="text"
            name="accountHolderName"
            value={form.accountHolderName}
            onChange={handleChange}
            readOnly={!editMode}
            className={`w-full p-2 bg-[#181818] border border-[#333] rounded text-white ${
              editMode ? "border-yellow-400" : ""
            }`}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            Account Number*
          </label>
          <input
            type="text"
            name="accountNumber"
            value={form.accountNumber}
            onChange={handleChange}
            readOnly={!editMode}
            className={`w-full p-2 bg-[#181818] border border-[#333] rounded text-white ${
              editMode ? "border-yellow-400" : ""
            }`}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Bank Name*</label>
          <input
            type="text"
            name="bankName"
            value={form.bankName}
            onChange={handleChange}
            readOnly={!editMode}
            className={`w-full p-2 bg-[#181818] border border-[#333] rounded text-white ${
              editMode ? "border-yellow-400" : ""
            }`}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            Bank Branch*
          </label>
          <input
            type="text"
            name="bankBranch"
            value={form.bankBranch}
            onChange={handleChange}
            readOnly={!editMode}
            className={`w-full p-2 bg-[#181818] border border-[#333] rounded text-white ${
              editMode ? "border-yellow-400" : ""
            }`}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-400 mb-1">IFSC Code*</label>
          <input
            type="text"
            name="ifscCode"
            value={form.ifscCode}
            onChange={handleChange}
            readOnly={!editMode}
            className={`w-full p-2 bg-[#181818] border border-[#333] rounded text-white ${
              editMode ? "border-yellow-400" : ""
            }`}
          />
        </div>
      </div>
    </div>
  );
}
